import { Button } from '@/components/button';
import { Widget } from '@/components/widget';
import { useEmitter } from '@/hooks/use-emitter';
import { useListener } from '@/hooks/use-listener';
import { useTabId } from '@/hooks/use-tab-id';
import { useScreenId } from '@/hooks/use-screen-id';
import React, { Suspense, useRef, useState } from 'react';
import { editor } from 'monaco-editor';
import { useUpdateAgentCode } from '@/features/agents/hooks/use-update-agent-code';
import MonacoEditor, { loader } from '@monaco-editor/react';
import { useForm } from 'react-hook-form';
import './code-editor-widget.css';
import { useTheme } from '@/hooks/use-theme';
import { useGetAgent } from '@/features/agents/hooks/use-get-agent';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import * as monaco from 'monaco-editor';
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker';
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker';
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker';
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker';
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker';

self.MonacoEnvironment = {
  getWorker(_, label) {
    if (label === 'json') {
      return new jsonWorker();
    }
    if (label === 'css' || label === 'scss' || label === 'less') {
      return new cssWorker();
    }
    if (label === 'html' || label === 'handlebars' || label === 'razor') {
      return new htmlWorker();
    }
    if (label === 'typescript' || label === 'javascript') {
      return new tsWorker();
    }
    return new editorWorker();
  },
};

loader.config({ monaco });

loader.init();

interface CodeEditorFormInputs {
  comment: string;
}

function applyObjectDecoration(editor: any, monaco: any) {
  const matchCases = editor
    ?.getModel()
    ?.findMatches('container:[a-z1234567890-]+', true, true, true, null, true);

  const searchResult = Array.from(matchCases || [], (code) => {
    return {
      range: new monaco.Range(
        code.range.startLineNumber,
        code.range.startColumn,
        code.range.endLineNumber,
        code.range.endColumn
      ),
      options: {
        className: 'editor-object',
        inlineClassName: 'editor-object-text',
        stickiness: 1,
      },
    };
  });

  editor.createDecorationsCollection(searchResult);
}

export function CodeEditorWidget({ id }: { id: string }) {
  const tabId = useTabId();
  const screenId = useScreenId();
  const idPrefix = `${tabId}/${screenId}`;
  const [sourceCode, setSourceCode] = useState<{
    content: string;
    language: string;
  }>({
    content: '',
    language: '',
  });
  const location = useLocation();
  const uuid = location.search.split('uuid=')[1];
  const [agentUuid, setAgentUuid] = useState<string>(uuid || '');
  const [state, setState] = useState<
    'loading' | 'success' | 'pending' | 'error'
  >('success');
  const emit = useEmitter({ id, listenersPrefix: idPrefix });
  const editorRef = useRef<editor.IStandaloneCodeEditor>();
  const monacoRef = useRef<any>();
  const updateAgentCodeMutation = useUpdateAgentCode();
  const { register, handleSubmit, reset } = useForm<CodeEditorFormInputs>();
  const { theme } = useTheme('light');
  const { data: agentData } = useGetAgent(agentUuid!);

  const showEditor = state === 'success' || state === 'pending';

  useListener({
    id,
    listenerFn: (data) => {
      setSourceCode(data.sourceCode);
      setAgentUuid(data.agentUuid);
      setState('success');

      if (data.agentUuid !== agentUuid) {
        window.history.replaceState(
          {},
          '',
          `/editor?connectTo=${id}&uuid=${data.agentUuid}`
        );
      }
    },
    type: 'source-code/success',
  });

  useListener({
    id,
    listenerFn: () => {
      setState('loading');
    },
    type: 'source-code/loading',
  });

  function handleChange() {
    if (editorRef.current) {
      applyObjectDecoration(editorRef.current, monacoRef.current);
    }

    setState('pending');
    emit({ type: 'source-code/pending' });
  }

  function handleSave(data: CodeEditorFormInputs) {
    updateAgentCodeMutation.mutate(
      {
        agentUuid,
        code: editorRef.current?.getValue() ?? '',
        comment: data.comment,
      },
      {
        onSuccess: () => {
          setState('success');
          emit({ type: 'source-code/success' });
          reset();
        },
      }
    );
  }

  function handleMount(editor: editor.IStandaloneCodeEditor, monaco: any) {
    editorRef.current = editor;
    monacoRef.current = monaco;

    applyObjectDecoration(editor, monaco);
  }

  return (
    <Widget title="Code Editor" id={id}>
      <form
        onSubmit={handleSubmit(handleSave)}
        className="flex flex-col h-full gap-4"
      >
        <div className="relative flex items-center gap-4">
          {agentData?.imageUrl ? (
            <img
              src={agentData?.imageUrl}
              alt=""
              className="w-10 h-10 rounded-full flex-none"
            />
          ) : (
            <div className="w-10 h-10 rounded-full flex-none bg-slate-50 dark:bg-gray-800 grid place-items-center">
              <PhotoIcon className="w-5 h-5 stroke-slate-500 dark:stroke-gray-400" />
            </div>
          )}
          <div className="block bg-slate-50 dark:bg-gray-800 w-full rounded-md border-0 py-2.5 px-3 text-gray-900 dark:text-gray-100 shadow-sm  sm:text-2xl sm:leading-6">
            {agentData?.title}
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400 absolute top-1/2 -translate-y-1/2 right-4">
            Rev {agentData?.revision}
          </span>
        </div>
        <div className="flex h-[calc(100%_-_42px)] min-h-[600px]">
          <Suspense
            fallback={<div className="dark:text-white">Loading...</div>}
          >
            {showEditor ? (
              <MonacoEditor
                language={sourceCode.language || agentData?.codeLanguage}
                value={sourceCode?.content?.trim() || agentData?.code}
                onChange={handleChange}
                onMount={handleMount}
                theme={theme === 'dark' ? 'vs-dark' : 'vs-light'}
              />
            ) : (
              <div className="dark:text-white">Loading...</div>
            )}
          </Suspense>
        </div>
        {state === 'pending' ? (
          <div className="flex gap-6 pt-1">
            <Button type="submit" loading={updateAgentCodeMutation.isLoading}>
              Save
            </Button>
            <input
              type="text"
              className="w-[360px] rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder="Describe your changes"
              {...register('comment')}
            />
          </div>
        ) : null}
      </form>
    </Widget>
  );
}
